import React from 'react';
import { AccumulatedGraph } from '../../types';

interface SubgraphToolbarProps {
  graph: AccumulatedGraph;
  onResetLayout: () => void;
  onClearUnpinned: () => void;
  onClearAll?: () => void;
  subgraphScope: 'none' | 'search_within' | 'expand_from';
  onSetScope: (scope: 'none' | 'search_within' | 'expand_from') => void;
  onCopyToClipboard?: () => void;
  onInsertIntoChat?: () => void;
  onExpand?: () => void;
  expandLabel?: string;
  expandTitle?: string;
  onSaveToGraphs?: () => void;
}

export const SubgraphToolbar: React.FC<SubgraphToolbarProps> = ({
  graph,
  onResetLayout,
  onClearUnpinned,
  onClearAll,
  subgraphScope,
  onSetScope,
  onCopyToClipboard,
  onInsertIntoChat,
  onExpand,
  expandLabel,
  expandTitle,
  onSaveToGraphs,
}) => {
  const nodeCount = graph.nodes.length;
  const edgeCount = graph.edges.length;

  return (
    <div className="malpedia-subgraph-toolbar-root">
      {/* Count badge */}
      <span className="malpedia-subgraph-toolbar-badge">
        {nodeCount} nodes, {edgeCount} edges
      </span>

      {/* Layout reset */}
      <button
        className="malpedia-subgraph-toolbar-btn"
        onClick={onResetLayout}
        title="Reset layout"
      >
        &#x27F3;
      </button>

      {/* Expand / collapse fullscreen */}
      {onExpand && (
        <button
          className="malpedia-subgraph-toolbar-btn"
          onClick={onExpand}
          title={expandTitle ?? 'Expand to fullscreen'}
        >
          {expandLabel ?? '\u26F6'}
        </button>
      )}

      {/* Scope toggle buttons */}
      <button
        className={`malpedia-subgraph-toolbar-btn ${
          subgraphScope === 'search_within' ? 'malpedia-subgraph-toolbar-btn--active' : ''
        }`}
        onClick={() => onSetScope(subgraphScope === 'search_within' ? 'none' : 'search_within')}
        title="Search within selected subgraph"
      >
        Within
      </button>
      <button
        className={`malpedia-subgraph-toolbar-btn ${
          subgraphScope === 'expand_from' ? 'malpedia-subgraph-toolbar-btn--active' : ''
        }`}
        onClick={() => onSetScope(subgraphScope === 'expand_from' ? 'none' : 'expand_from')}
        title="Expand from selected nodes"
      >
        Expand
      </button>

      {/* Graph-to-prompt */}
      {onCopyToClipboard && (
        <button
          className="malpedia-subgraph-toolbar-btn"
          onClick={onCopyToClipboard}
          title="Copy graph context as markdown"
        >
          Copy
        </button>
      )}
      {onInsertIntoChat && (
        <button
          className="malpedia-subgraph-toolbar-btn"
          onClick={onInsertIntoChat}
          title="Insert graph context into chat"
        >
          Prompt
        </button>
      )}

      {/* Save to Saved Graphs */}
      {onSaveToGraphs && (
        <button
          className="malpedia-subgraph-toolbar-btn"
          onClick={onSaveToGraphs}
          title="Save to Saved Graphs"
        >
          Save
        </button>
      )}

      {/* Clear unpinned */}
      <button
        className="malpedia-subgraph-toolbar-btn"
        onClick={onClearUnpinned}
        title="Clear unpinned nodes"
      >
        Clear
      </button>

      {/* Clear all (standalone mode) */}
      {onClearAll && (
        <button
          className="malpedia-subgraph-toolbar-btn"
          onClick={onClearAll}
          title="Clear all nodes and reset graph"
        >
          Clear All
        </button>
      )}
    </div>
  );
};
